import { styled } from '@mui/material';
import MuiCheckbox, { CheckboxProps } from '@mui/material/Checkbox';
import { forwardRef } from 'react';

import CheckSvg from '@/components/icons/svg/check.svg?react';
import { COLORS } from '@/styles/tokens/colors';

import { FormControl } from '../FormControl';
import {
  BaseCheckboxInputProps,
  FormControlCheckboxInputProps,
  HelpTextVariant,
} from '../inputTypes';

interface CheckboxInputProps extends FormControlCheckboxInputProps {
  id?: string;
  errorMessage?: string;
  helpText?: string;
  helpTextVariant?: HelpTextVariant;
  icon?: React.ReactNode;
  checkedIcon?: React.ReactNode;
  label?: React.ReactNode;
  contextualHelp?: JSX.Element;
  indeterminate?: boolean;
  hideLabel?: boolean;
  /**
   * Accessible name for the checkbox, for the cases where no visible label is
   * associated with it (e.g. a checkbox alone in a table column).
   */
  ariaLabel?: string;
}

function CheckboxIcon({
  size,
  selected = false,
  indeterminate = false,
}: Pick<CheckboxProps, 'size' | 'indeterminate'> & {
  selected?: boolean;
}) {
  // Keep the MUI icon's outer space so labels and click areas do not move.
  let dimension = 18;
  if (size) {
    dimension = size === 'small' ? 16 : 20;
  }
  const inset = dimension / 6;
  const outerSize = dimension + inset * 2;
  const filled = selected || indeterminate;

  return (
    <svg
      aria-hidden="true"
      width={outerSize}
      height={outerSize}
      viewBox={`0 0 ${outerSize} ${outerSize}`}
    >
      <rect
        x={inset + (filled ? 0 : 0.5)}
        y={inset + (filled ? 0 : 0.5)}
        width={dimension - (filled ? 0 : 1)}
        height={dimension - (filled ? 0 : 1)}
        rx={filled ? 4 : 3.5}
        fill={filled ? 'currentColor' : COLORS.PRIMITIVES.WHITE}
        stroke={filled ? 'none' : 'currentColor'}
      />
      {filled && (
        <svg
          x={inset + 2}
          y={inset + 2}
          width={dimension - 4}
          height={dimension - 4}
          viewBox="0 0 24 24"
          fill="white"
        >
          {indeterminate ? (
            <path d="M5.5 11.25h13v1.5h-13z" />
          ) : (
            <CheckSvg width={24} height={24} color="white" />
          )}
        </svg>
      )}
    </svg>
  );
}

export const StyledCheckbox = styled(
  forwardRef<
    HTMLButtonElement,
    Omit<CheckboxProps, 'value'> &
      Partial<Pick<CheckboxInputProps, 'value' | 'error' | 'ariaLabel'>>
  >(function StyledCheckbox(props, ref) {
    const {
      error: _error,
      size,
      id,
      indeterminate,
      ariaLabel,
      inputProps,
      ...rest
    } = props;

    return (
      <MuiCheckbox
        ref={ref}
        disableRipple
        icon={<CheckboxIcon size={size} />}
        checkedIcon={<CheckboxIcon size={size} selected />}
        indeterminateIcon={<CheckboxIcon size={size} indeterminate />}
        checked={props.value ?? false}
        indeterminate={indeterminate}
        inputProps={
          {
            'data-testid': `checkbox-${props.name ?? ''}`,
            id,
            'aria-label': ariaLabel,
            ...inputProps,
          } as React.InputHTMLAttributes<HTMLInputElement>
        }
        {...rest}
      />
    );
  })
)(({ size }) => ({
  color: COLORS.GRAY[500],
  // the native input has a lot of padding around it because of the treatment of the MUI
  // "ripple" interaction. we disable that interaction, so removing the padding to allow
  // consumers of this more flexibility around styling
  padding: '0 !important',
  zIndex: 2,
  borderRadius: '4px',
  '&.Mui-checked, &.MuiCheckbox-indeterminate': {
    color: COLORS.GRAY[700],
  },
  '&:hover:not(.Mui-disabled)': {
    color: COLORS.GRAY[800],
    backgroundColor: 'transparent',
  },
  '&.Mui-disabled': {
    color: COLORS.GRAY[300],
  },
  '&.Mui-focusVisible': {
    outline: `1px solid ${COLORS.GRAY[500]}`,
    outlineOffset: 0,
  },
  ...(size && {
    // Keep the existing size for caller-supplied MUI icons.
    '& .MuiSvgIcon-root': {
      fontSize: ((size === 'small' ? 16 : 20) * 24) / 18,
    },
  }),
}));
export function Checkbox({
  errorMessage,
  helpText,
  helpTextVariant,
  contextualHelp,
  label,
  hideLabel,
  ...inputProps
}: CheckboxInputProps) {
  return (
    <FormControl<BaseCheckboxInputProps>
      component={StyledCheckbox}
      hideLabel={hideLabel}
      inputProps={inputProps}
      labelPosition="right"
      errorMessage={errorMessage}
      helpText={helpText}
      helpTextVariant={helpTextVariant}
      contextualHelp={contextualHelp}
      id={inputProps.id}
      label={label}
      containerSx={
        inputProps.size
          ? {
              '& label': {
                fontSize: inputProps.size === 'small' ? 12 : 14,
                lineHeight: inputProps.size === 'small' ? '16px' : '20px',
              },
            }
          : undefined
      }
    />
  );
}
